import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getUser, setNameTestState } from "@/lib/storage";
import { fmtDate } from "@/lib/exposure";
import { BrandHeader } from "@/components/BrandHeader";
import { LoadingScreen } from "@/components/LoadingScreen";

const QUESTIONS = [
  { id: "name", type: "text", question: "What's the name you want me to evaluate?", helper: "Could be a business name, course name, podcast title, product line — whatever brand asset you're working on.", placeholder: "e.g., Steady State Coffee" },
  { id: "asset_type", type: "single", question: "What kind of brand asset is this?", options: ["A business or company name", "A course or program", "A podcast or show", "A product or product line", "A signature framework or method", "A book or book series", "Something else"] },
  { id: "what_it_does", type: "textarea", question: "In one sentence, what does this brand do or stand for?", helper: "Plain English. The clearer you are, the better my analysis.", placeholder: "Specialty coffee subscriptions for remote workers." },
  { id: "ideal_for", type: "textarea", question: "Who's the ideal person this brand is for?", helper: "Be specific if you can. 'Everyone' won't help me give you good feedback.", placeholder: "Remote knowledge workers in their 30s who care about ritual." },
  { id: "use_stage", type: "single", question: "Is this name already in use, or are you considering it for the future?", options: ["Already using it actively in my business", "Soft-launched, testing it out", "Decided on it but haven't launched yet", "Still considering — haven't committed yet", "Brainstorming options — this is one of several"] },
];

const scoreColor = (s) => s >= 75 ? "var(--evergreen)" : s >= 50 ? "#ea580c" : "#b91c1c";

// Modes: 'intro' | 'question' | 'loading' | 'report' | 'past' | 'compare'

export const NameTest = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [draft, setDraft] = useState("");
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [pastReport, setPastReport] = useState(null);
  const [pastName, setPastName] = useState(null);
  const [pastDate, setPastDate] = useState(null);
  const [compareIds, setCompareIds] = useState([]); // ids selected for side-by-side
  const [compareData, setCompareData] = useState([]); // hydrated reports

  useEffect(() => {
    if (!getUser()) { navigate("/", { replace: true }); return; }
    refreshHistory();
  }, [navigate]);

  const refreshHistory = async () => {
    try {
      const res = await api.get("/name-test/history");
      setHistory(res.data?.history || []);
    } catch { /* enhancement */ }
  };

  const q = QUESTIONS[idx];
  const total = QUESTIONS.length;

  const start = () => { setMode("question"); setIdx(0); setAnswers({}); setDraft(""); setReport(null); };

  const viewPast = async (id) => {
    try {
      const res = await api.get(`/name-test/${id}`);
      setPastReport(res.data.report);
      setPastName((res.data.answers || {}).name);
      setPastDate(res.data.created_at);
      setMode("past");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Couldn't load that past evaluation.");
    }
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const runCompare = async () => {
    if (compareIds.length < 2) return;
    try {
      const results = await Promise.all(compareIds.map((id) => api.get(`/name-test/${id}`).then(r => r.data)));
      setCompareData(results);
      setMode("compare");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Couldn't load all selected names.");
    }
  };

  const valid = () => draft && draft.toString().trim().length > 0;

  const next = async () => {
    if (!valid()) return;
    const newAnswers = { ...answers, [q.id]: draft };
    setAnswers(newAnswers);

    if (idx + 1 >= total) {
      setMode("loading");
      try {
        const payload = {
          name: newAnswers.name,
          asset_type: newAnswers.asset_type,
          what_it_does: newAnswers.what_it_does,
          ideal_for: newAnswers.ideal_for,
          use_stage: newAnswers.use_stage,
        };
        const res = await api.post(`/name-test/report`, payload);
        setReport(res.data.report);
        setNameTestState({ completed: true, lastName: newAnswers.name, completedAt: new Date().toISOString() });
        setMode("report");
        refreshHistory();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't generate report. Please try again.");
        setMode("question");
      }
    } else {
      setIdx(idx + 1);
      setDraft("");
    }
  };

  const back = () => {
    if (idx === 0) { setMode("intro"); return; }
    const prevIdx = idx - 1;
    setIdx(prevIdx);
    setDraft(answers[QUESTIONS[prevIdx].id] || "");
  };

  const progress = Math.round((idx / total) * 100);

  return (
    <div data-testid="name-test-page">
      <BrandHeader />

      {mode === "intro" && (
        <NameTestIntro
          onStart={start}
          history={history}
          onViewPast={viewPast}
          compareIds={compareIds}
          onToggleCompare={toggleCompare}
          onRunCompare={runCompare}
        />
      )}

      {mode === "question" && (
        <section style={{ padding: "2rem 0 4rem" }}>
          <div className="container-narrow">
            <p className="eyebrow" data-testid="name-test-progress-label">Question {idx + 1} of {total}</p>
            <div className="progress-bar" style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}>
              <span style={{ width: `${progress}%` }} />
            </div>
            <h2 style={{ fontSize: "clamp(1.3rem, 3.5vw, 1.7rem)", margin: "0 0 0.6rem", lineHeight: 1.25 }} data-testid="name-test-question-text">
              {q.question}
            </h2>
            {q.helper && <p style={{ color: "var(--cobalt)", fontSize: "0.92rem", margin: "0 0 1.25rem" }}>{q.helper}</p>}
            {q.type === "text" && (
              <input type="text" className="brand-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={q.placeholder} data-testid={`name-test-input-${q.id}`} autoFocus />
            )}
            {q.type === "textarea" && (
              <textarea className="brand-input" rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={q.placeholder} data-testid={`name-test-input-${q.id}`} style={{ resize: "vertical", fontFamily: "Arial, sans-serif" }} autoFocus />
            )}
            {q.type === "single" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {q.options.map((opt, i) => (
                  <button key={opt} type="button" className={`option-btn ${draft === opt ? "selected" : ""}`} onClick={() => setDraft(opt)} data-testid={`name-test-option-${q.id}-${i}`}>
                    <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="btn-yellow" onClick={next} disabled={!valid()} data-testid="name-test-next-button">
                {idx + 1 >= total ? "Evaluate the name →" : "Next →"}
              </button>
              <button type="button" className="btn-outline-navy" onClick={back} data-testid="name-test-back-button">
                ← Back
              </button>
            </div>
          </div>
        </section>
      )}

      {mode === "loading" && <LoadingScreen brandName={answers.name} />}

      {mode === "report" && report && (
        <NameTestReport report={report} name={answers.name} dateLabel={fmtDate(new Date().toISOString())} isPast={false} onRetake={start} />
      )}

      {mode === "past" && pastReport && (
        <NameTestReport
          report={pastReport}
          name={pastName}
          dateLabel={fmtDate(pastDate)}
          isPast={true}
          onRetake={start}
          onBackToIntro={() => { setMode("intro"); setPastReport(null); setPastName(null); setPastDate(null); }}
        />
      )}

      {mode === "compare" && compareData.length > 0 && (
        <CompareView
          items={compareData}
          onBack={() => { setMode("intro"); setCompareData([]); setCompareIds([]); }}
        />
      )}
    </div>
  );
};

const NameTestIntro = ({ onStart, history, onViewPast, compareIds, onToggleCompare, onRunCompare }) => {
  const hasHistory = history && history.length > 0;
  return (
    <section style={{ padding: "3rem 0 4rem" }}>
      <div className="container-narrow">
        <p className="eyebrow">Test a name</p>
        <h1 style={{ fontSize: "clamp(2rem, 5vw, 2.6rem)", margin: "0.3rem 0 1.2rem", letterSpacing: "-0.02em" }} data-testid="name-test-intro-title">
          The Name Evaluator
        </h1>
        <div style={{ fontSize: "1.05rem", lineHeight: 1.65 }}>
          <p>Quick truth: a great name is one of the most undervalued business decisions you can make.</p>
          <p>Here's the deal — I'll evaluate any name you give me across four dimensions that actually matter:</p>
          <ul style={{ paddingLeft: "1.2rem" }}>
            <li><strong>Memorability</strong> — does it stick in people's heads?</li>
            <li><strong>Distinctiveness / Trademark Strength</strong> — is it the kind of name you can actually own?</li>
            <li><strong>Conflict Risk</strong> — any obvious red flags in the trademark world?</li>
            <li><strong>Future Fit</strong> — will this name still serve you in 5 years?</li>
          </ul>
          <p>Test as many names as you want. I'll save every one so you can come back and compare them later.</p>
        </div>
        <button type="button" className="btn-yellow" onClick={onStart} data-testid="name-test-start-button" style={{ marginTop: "1.5rem" }}>
          {hasHistory ? "Evaluate a new name →" : "Let's evaluate a name →"}
        </button>

        {hasHistory && (
          <div style={{ marginTop: "3rem" }} data-testid="name-test-history-section">
            <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 1.1rem" }}>
              Your past evaluations
            </h2>
            <p style={{ margin: "0 0 1rem", color: "var(--midnight)" }}>
              You've evaluated <strong>{history.length}</strong> {history.length === 1 ? "name" : "names"} so far. Click any one to re-read it — or check the boxes on two or more and compare them side by side.
            </p>

            {compareIds.length >= 2 && (
              <div style={{ marginBottom: "1rem" }}>
                <button type="button" className="btn-yellow" onClick={onRunCompare} data-testid="name-test-compare-button">
                  Compare {compareIds.length} names side by side →
                </button>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
              {history.map((h) => {
                const score = typeof h.overall_score === "number" ? h.overall_score : null;
                const color = score == null ? "var(--cobalt)" : scoreColor(score);
                const isSelected = compareIds.includes(h.id);
                return (
                  <div
                    key={h.id}
                    data-testid={`name-test-history-item-${h.id}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: "0.7rem",
                      background: "var(--warm-white)",
                      border: "2px solid var(--navy)",
                      borderLeft: `8px solid ${color}`,
                      padding: "0.9rem 1rem",
                      boxShadow: "3px 3px 0 0 var(--navy)",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", cursor: "pointer", paddingTop: 4 }}
                      title="Select to compare"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleCompare(h.id)}
                        data-testid={`name-test-compare-checkbox-${h.id}`}
                        style={{ width: 18, height: 18, accentColor: "var(--navy)", cursor: "pointer" }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => onViewPast(h.id)}
                      style={{ textAlign: "left", background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
                        {score != null && (
                          <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "0.2rem 0.5rem", background: color, color: "var(--warm-white)", letterSpacing: "0.04em" }}>
                            {score}/100
                          </span>
                        )}
                        <span style={{ color: "var(--navy)", fontWeight: 700, fontSize: "1.02rem" }}>
                          "{h.name}"
                        </span>
                        <span style={{ color: "var(--cobalt)", fontWeight: 700, fontSize: "0.82rem" }}>
                          · {fmtDate(h.created_at)}
                        </span>
                      </div>
                      {h.headline && (
                        <div style={{ color: "var(--midnight)", fontSize: "0.93rem", lineHeight: 1.45 }}>
                          {h.headline}
                        </div>
                      )}
                      <div style={{ marginTop: "0.3rem", color: "var(--cobalt)", fontWeight: 700, fontSize: "0.82rem" }}>
                        View full report →
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const NameTestReport = ({ report, name, dateLabel, isPast, onRetake, onBackToIntro }) => {
  const overall = report.overall_score ?? 0;
  const overallColor = scoreColor(overall);

  return (
    <section style={{ padding: "2.5rem 0 4rem" }} data-testid="name-test-report">
      <div className="container-narrow">
        {isPast && (
          <div className="callout" style={{ marginBottom: "1.5rem", borderTopColor: "var(--cobalt)" }} data-testid="name-test-viewing-past-banner">
            <p style={{ margin: 0 }}>
              <strong>Viewing your evaluation of "{name}" from {dateLabel}.</strong>{" "}
              {onBackToIntro && (
                <button type="button" onClick={onBackToIntro} style={{ background: "none", border: "none", padding: 0, color: "var(--cobalt)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }} data-testid="name-test-back-to-intro">
                  ← Back to all
                </button>
              )}
            </p>
          </div>
        )}

        <p className="eyebrow">Name Evaluator · {name}</p>
        <h1 style={{ fontSize: "clamp(1.7rem, 4.5vw, 2.3rem)", margin: "0.3rem 0 1.5rem", letterSpacing: "-0.01em", lineHeight: 1.2 }} data-testid="name-test-headline">
          {report.headline}
        </h1>

        <div className="callout" style={{ marginBottom: "1.5rem" }}>
          <p className="eyebrow">Overall Power Score</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", margin: "0.4rem 0 0.8rem" }}>
            <span style={{ fontSize: "3rem", fontWeight: 700, color: "var(--navy)", lineHeight: 1 }} data-testid="name-test-overall-score">
              {overall}
            </span>
            <span style={{ fontSize: "1.1rem", color: "var(--midnight)", fontWeight: 700 }}>/ 100</span>
          </div>
          <div className="score-bar-track">
            <div className="score-bar-fill" style={{ width: `${overall}%`, background: overallColor }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
          {[
            { key: "memorability", label: "Memorability" },
            { key: "distinctiveness", label: "Trademark Strength" },
            { key: "conflict_risk", label: "Conflict Risk" },
            { key: "future_fit", label: "Future Fit" },
          ].map(({ key, label }) => {
            const s = report.sub_scores?.[key] ?? 0;
            const c = s >= 18 ? "var(--evergreen)" : s >= 12 ? "#ea580c" : "#b91c1c";
            return (
              <div key={key} style={{ background: "var(--paper)", border: "1px solid var(--hairline)", padding: "1rem", borderRadius: 3 }} data-testid={`name-test-sub-${key}`}>
                <p className="eyebrow" style={{ margin: 0 }}>{label}</p>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.3rem", margin: "0.3rem 0" }}>
                  <span style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--navy)" }}>{s}</span>
                  <span style={{ color: "var(--midnight)", fontSize: "0.9rem" }}>/ 25</span>
                </div>
                <div className="score-bar-track" style={{ height: 8 }}>
                  <div className="score-bar-fill" style={{ width: `${(s / 25) * 100}%`, background: c }} />
                </div>
              </div>
            );
          })}
        </div>

        {[
          { key: "memorability", label: "Memorability" },
          { key: "distinctiveness", label: "Trademark Strength" },
          { key: "conflict_risk", label: "Conflict Risk" },
          { key: "future_fit", label: "Future Fit" },
        ].map(({ key, label }) => (
          <div key={key} style={{ marginBottom: "1.5rem" }}>
            <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.7rem" }}>
              {label}
            </h2>
            <p style={{ margin: 0 }} data-testid={`name-test-dim-${key}`}>{report.dimensions?.[key]}</p>
          </div>
        ))}

        <div style={{ marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.7rem" }}>
            What this means in plain English
          </h2>
          <p style={{ margin: 0 }} data-testid="name-test-plain">{report.plain_english}</p>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.7rem" }}>
            Your next 3 moves
          </h2>
          <ol style={{ paddingLeft: "1.2rem", margin: 0 }} data-testid="name-test-moves">
            {report.next_moves?.map((m, i) => (
              <li key={i} style={{ marginBottom: "0.8rem" }}>
                <strong>{m.title}</strong>
                <div>{m.body}</div>
              </li>
            ))}
          </ol>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.7rem" }}>
            The bottom line
          </h2>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }} data-testid="name-test-bottom">{report.bottom_line}</p>
        </div>

        <div className="callout" style={{ marginTop: "1.5rem" }}>
          <p style={{ margin: 0, fontStyle: "italic" }} data-testid="name-test-disclaimer">
            {report.disclaimer}
          </p>
        </div>

        <div style={{ marginTop: "2.5rem", display: "flex", flexDirection: "column", gap: "1.2rem" }}>
          <div>
            <p style={{ margin: "0 0 0.5rem" }}>
              Ready to learn how to actually trademark a name like <strong>{name}</strong>?
            </p>
            <a
              href="https://indielaw.easywebinar.live/gettrademarked"
              target="_blank" rel="noopener noreferrer"
              className="btn-yellow"
              data-testid="name-test-webinar-cta"
            >
              Join my free training: Protect Your Brand & Get Legally Legit →
            </a>
          </div>
          <div className="divider-rule" />
          <div>
            <p style={{ margin: "0 0 0.5rem" }}>Want to evaluate another name?</p>
            <button type="button" className="btn-outline-navy" onClick={onRetake} data-testid="name-test-retake-cta">
              Run another name through the Name Evaluator →
            </button>
          </div>
          <div className="callout">
            <p style={{ margin: 0 }}>
              Want to go deeper on naming and trademarks? My book <em>Legally Legit</em> covers all of this and more.{" "}
              <a href="https://a.co/d/0isDjKfG" target="_blank" rel="noopener noreferrer" data-testid="name-test-book-link">
                Get it on Amazon →
              </a>
            </p>
          </div>
          <Link to="/dashboard" data-testid="name-test-back-to-dashboard">← Back to dashboard</Link>
        </div>
      </div>
    </section>
  );
};

const CompareView = ({ items, onBack }) => {
  const dimensions = useMemo(() => ([
    { key: "memorability", label: "Memorability" },
    { key: "distinctiveness", label: "Trademark Strength" },
    { key: "conflict_risk", label: "Conflict Risk" },
    { key: "future_fit", label: "Future Fit" },
  ]), []);

  // Highlight the highest score per dimension
  const winnerByDim = useMemo(() => {
    const w = {};
    dimensions.forEach(({ key }) => {
      let bestIdx = -1, bestScore = -1;
      items.forEach((it, i) => {
        const s = it.report?.sub_scores?.[key] ?? -1;
        if (s > bestScore) { bestScore = s; bestIdx = i; }
      });
      w[key] = bestIdx;
    });
    return w;
  }, [items, dimensions]);

  const overallWinner = useMemo(() => {
    let bestIdx = -1, best = -1;
    items.forEach((it, i) => {
      const s = it.report?.overall_score ?? -1;
      if (s > best) { best = s; bestIdx = i; }
    });
    return bestIdx;
  }, [items]);

  return (
    <section style={{ padding: "2.5rem 0 4rem" }} data-testid="name-test-compare-view">
      <div className="container-wide">
        <button type="button" onClick={onBack} className="btn-outline-navy" data-testid="name-test-compare-back" style={{ marginBottom: "1.2rem" }}>
          ← Back to all evaluations
        </button>
        <p className="eyebrow">Side-by-side comparison</p>
        <h1 style={{ fontSize: "clamp(1.7rem, 4.5vw, 2.3rem)", margin: "0.3rem 0 1.5rem", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
          {items.length} names, head-to-head.
        </h1>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }} data-testid="name-test-compare-table">
            <thead>
              <tr>
                <th style={thStyle()}></th>
                {items.map((it, i) => (
                  <th key={it.id} style={{ ...thStyle(), background: i === overallWinner ? "rgba(0,103,71,0.08)" : undefined }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: "1.05rem", color: "var(--navy)" }}>"{it.answers?.name || "—"}"</span>
                      <span style={{ fontSize: "0.72rem", color: "var(--cobalt)", textTransform: "none", fontWeight: 400, letterSpacing: 0 }}>
                        {fmtDate(it.created_at)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle("strong")}>Overall</td>
                {items.map((it, i) => {
                  const s = it.report?.overall_score ?? 0;
                  return (
                    <td key={it.id} style={{ ...tdStyle(), background: i === overallWinner ? "rgba(0,103,71,0.08)" : undefined }}>
                      <span style={{ fontWeight: 700, color: scoreColor(s), fontSize: "1.4rem" }}>{s}</span>
                      <span style={{ color: "var(--cobalt)", fontSize: "0.85rem" }}>/100</span>
                      {i === overallWinner && <span style={{ marginLeft: 6, fontSize: "0.7rem", padding: "2px 6px", background: "var(--evergreen)", color: "white", fontWeight: 700, letterSpacing: "0.04em" }}>BEST</span>}
                    </td>
                  );
                })}
              </tr>
              {dimensions.map(({ key, label }) => (
                <tr key={key}>
                  <td style={tdStyle("strong")}>{label}</td>
                  {items.map((it, i) => {
                    const s = it.report?.sub_scores?.[key] ?? 0;
                    const win = i === winnerByDim[key];
                    return (
                      <td key={it.id} style={{ ...tdStyle(), background: win ? "rgba(0,103,71,0.06)" : undefined }}>
                        <span style={{ fontWeight: 700, color: "var(--navy)" }}>{s}</span>
                        <span style={{ color: "var(--cobalt)", fontSize: "0.85rem" }}>/25</span>
                        {win && <span style={{ marginLeft: 6, color: "var(--evergreen)", fontWeight: 700 }}>★</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td style={tdStyle("strong")}>Headline</td>
                {items.map((it) => (
                  <td key={it.id} style={tdStyle()}>{it.report?.headline}</td>
                ))}
              </tr>
              {dimensions.map(({ key, label }) => (
                <tr key={`dim-${key}`}>
                  <td style={tdStyle("strong")}>{label} note</td>
                  {items.map((it) => (
                    <td key={it.id} style={{ ...tdStyle(), fontSize: "0.88rem", lineHeight: 1.5 }}>
                      {it.report?.dimensions?.[key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td style={tdStyle("strong")}>Bottom line</td>
                {items.map((it) => (
                  <td key={it.id} style={{ ...tdStyle(), fontSize: "0.88rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {it.report?.bottom_line || "—"}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const thStyle = () => ({
  textAlign: "left",
  padding: "0.7rem 0.9rem",
  borderBottom: "2px solid var(--navy)",
  background: "var(--warm-white)",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--navy)",
  verticalAlign: "bottom",
});

const tdStyle = (strong) => ({
  padding: "0.7rem 0.9rem",
  borderBottom: "1px solid var(--hairline)",
  verticalAlign: "top",
  color: strong ? "var(--cobalt)" : "var(--midnight)",
  fontWeight: strong ? 700 : 400,
  textTransform: strong ? "uppercase" : "none",
  letterSpacing: strong ? "0.05em" : 0,
  fontSize: strong ? "0.75rem" : "0.95rem",
});
